import React from 'react';
import { ArrowDown, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';

const Hero = ({ personal }) => {
  const { t, language } = useLanguage();

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleResumeDownload = () => {
    // German UI -> German CV, every other language -> English CV
    const isGerman = language === 'de';
    const fileUrl = isGerman
      ? '/cv/Tymur-Abkiliamov-Lebenslauf-DE.pdf'
      : '/cv/Tymur-Abkiliamov-CV-EN.pdf';
    const fileName = isGerman
      ? 'Tymur Abkiliamov - Lebenslauf.pdf'
      : 'Tymur Abkiliamov - CV.pdf';

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative bg-background overflow-hidden">
      {/* Background gradient blobs */}
      <div className="absolute inset-0">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-blue-500/8 to-teal-500/8 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-gradient-to-tr from-teal-500/8 to-blue-500/8 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-blue-500/3 to-teal-500/3 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-8 relative z-10">
        <div className="text-center">
          {/* Profile image with glow ring */}
          <div className="mb-8 relative inline-block animate-fadeInUp" style={{ animationDelay: '0ms', animationFillMode: 'both' }}>
            <div className="absolute inset-0 w-40 h-40 md:w-48 md:h-48 rounded-full bg-gradient-to-tr from-blue-500 via-teal-400 to-blue-500 opacity-60 blur-lg mx-auto animate-gradient-shift" style={{ backgroundSize: '200% 200%' }} />
            <img
              src={personal.profileImage}
              alt={personal.name}
              className="relative w-40 h-40 md:w-48 md:h-48 rounded-full object-cover mx-auto shadow-2xl ring-4 ring-white/90 dark:ring-gray-800/90 z-10"
            />
          </div>

          {/* Greeting badge */}
          <div className="animate-fadeInUp" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
            <p className="text-sm md:text-base text-muted-foreground font-medium tracking-widest uppercase mb-4">
              {t('hero.greeting')}
            </p>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-foreground mb-6 tracking-tight animate-fadeInUp" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
            <span className="block font-semibold bg-gradient-to-r from-blue-600 via-blue-700 to-teal-500 bg-clip-text text-transparent">
              {personal.name}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-muted-foreground mb-4 max-w-3xl mx-auto font-light animate-fadeInUp" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
            {t('hero.title')}
          </p>

          {/* Description */}
          <p className="text-lg md:text-xl text-muted-foreground/80 mb-12 max-w-2xl mx-auto leading-relaxed animate-fadeInUp" style={{ animationDelay: '400ms', animationFillMode: 'both' }}>
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fadeInUp" style={{ animationDelay: '500ms', animationFillMode: 'both' }}>
            <Button
              onClick={scrollToAbout}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base font-medium transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30"
            >
              {t('hero.viewWork')}
            </Button>
            <Button
              onClick={handleResumeDownload}
              variant="outline"
              size="lg"
              className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-8 py-3 text-base font-medium transition-all duration-200"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('hero.resume')}
            </Button>
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center animate-fadeInUp" style={{ animationDelay: '600ms', animationFillMode: 'both' }}>
            <button
              onClick={scrollToAbout}
              className="p-2 rounded-full border border-border hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 animate-scroll-hint"
              aria-label="Scroll to next section"
            >
              <ArrowDown className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
